import { API, graphqlOperation } from "aws-amplify";
import {
  CreateLLMProjectMutation,
  CreateLLMUserMutation,
  CreateLLMUsageTierMutation,
  LLMUser,
  LLMUsageTierStatus,
  LLMPeriod,
  LLMReplenishmentMethod,
  LLMUsageLimitAction,
} from "../API";
import {
  createLLMProject,
  createLLMUser,
  createLLMUsageTier,
} from "../graphql/mutations";
import { GraphQLResult } from "@aws-amplify/api-graphql";
import {
  anthropic_bodyTemplate,
  anthropic_EndpointTemplate,
  anthropic_headerTemplate,
  anthropic_inputPricePerMillion,
  anthropic_outputPricePerMillion,
  google_bodyTemplate,
  google_EndpointTemplate,
  google_headerTemplate,
  google_inputPricePerMillion,
  google_outputPricePerMillion,
  openAI_bodyTemplate,
  openAI_EndpointTemplate,
  openAI_headerTemplate,
  openAI_inputPricePerMillion,
  openAI_outputPricePerMillion,
} from "../modelTemplates";
import { createLLMServiceAfterEdit } from "./createLLMServiceAfterEdit";
import { createLLMGroupAfterEdit } from "./createLLMGroupAfterEdit";
import { version } from "os";

export async function createNewLLMUserAndLLMProject(
  userEmail: string,
  projectName: string,
  userDisplayName: string,
  userAcceptTerms: boolean,
  userAcceptGDPR: boolean
) {
  let newUser: LLMUser | undefined = undefined;

  const projectCreate = async (): Promise<
    GraphQLResult<CreateLLMProjectMutation>
  > => {
    // create a random group number 1 to 12
    const randomGroup = Math.floor(Math.random() * 12) + 1;

    return API.graphql(
      graphqlOperation(createLLMProject, {
        input: {
          name: projectName,
          status: "active",
          description: "Manages LLM features for " + projectName,
          cacheResponsesForSeconds: 3600,
          loadSheddingTier: 0,
          newCustomerLoadSheddingTier: 2,
          maintenanceMode: false,
          maintenanceModeMessage:
            "We are currently performing maintenance on this service. Please try again later.",
          loadBalancingMode: "proportion",
          systemInstructions:
            "Please follow these guidelines when generating responses:\n1. **Respect and Inclusivity**: Be respectful and avoid hate speech, racism, sexism, or discriminatory language. Use gender-neutral terms and avoid assumptions about personal characteristics.\n2. **Safety and Appropriateness**: Ensure responses are safe and suitable for all audiences; avoid harmful or dangerous advice.\n3. **Accuracy**: Provide accurate information. If unsure, indicate the need for more information or state the uncertainty.\n4. **Confidentiality**: Do not share personal or sensitive information; respect privacy and confidentiality.",
          piiRedactionScope: "",
          piiScope: "",
          piiGroupId: "",
          piiMessage: "Prompt contains PII data.",
          promptBlacklist: "",
          blacklistGroupId: "",
          blacklistMessage: "Prompt contains blacklisted words.",
          safety: 0,
          safetyGroupId: "",
          safetyMessage: "Prompt contains unsafe language.",
          strongModelGroupId: "",
          routeModel: "none",
          lastLogGeneratedAt: new Date().toISOString(),
          logInterval: "none",
          logfileRetentionPeriod: 90,
          callRetentionPeriod: 60,
          randomGroup: randomGroup,
          automaticModelManagement: true,
          trialTokensRemaining: 1000000,
          newCustomerUsageTierId: "",
        },
      })
    ) as GraphQLResult<CreateLLMProjectMutation>;
  };

  try {
    const projectDataResult = await projectCreate();

    if (
      projectDataResult &&
      projectDataResult.data &&
      projectDataResult.data.createLLMProject
    ) {
      const projectId = projectDataResult.data.createLLMProject.id ?? "";

      if (projectId !== "") {
        const userCreate = async (): Promise<
          GraphQLResult<CreateLLMUserMutation>
        > => {
          return API.graphql(
            graphqlOperation(createLLMUser, {
              input: {
                projectId: projectId,
                email: userEmail.toLowerCase() ?? "",
                displayName: userDisplayName ?? "",
                acceptedTermsOfService: userAcceptTerms,
                acceptedGDPRTerms: userAcceptGDPR,
                status: "active",
              },
            })
          ) as GraphQLResult<CreateLLMUserMutation>;
        };

        const userDataResult = await userCreate();
        if (
          userDataResult &&
          userDataResult.data &&
          userDataResult.data.createLLMUser
        ) {
          newUser = userDataResult.data?.createLLMUser as LLMUser;
        }

        // create a Default usage tier record for optional future use,
        // but do not auto-assign it as the default for new customers.
        const usageTierCreate = async (): Promise<
          GraphQLResult<CreateLLMUsageTierMutation>
        > => {
          return API.graphql(
            graphqlOperation(createLLMUsageTier, {
              input: {
                projectId: projectId,
                status: LLMUsageTierStatus.ACTIVE,
                version: 1,
                activeAt: new Date().toISOString(),
                name: "Default Usage Tier",
                description: "Default usage tier for new customers.",
                replenishmentPeriod: LLMPeriod.MONTHLY,
                replenishmentMethod: LLMReplenishmentMethod.RESET,
                usageLimitAction: LLMUsageLimitAction.BLOCK,
                standardTokenReplenishmentAmount: 0,
                premiumTokenReplenishmentAmount: 0,
                superPremiumTokenReplenishmentAmount: 0,
                reasoningTokenReplenishmentAmount: 0,
                standardCallReplenishmentAmount: 500,
                premiumCallReplenishmentAmount: 250,
                superPremiumCallReplenishmentAmount: 0,
                reasoningCallReplenishmentAmount: 250,
                standardTokenUnlimited: true,
                premiumTokenUnlimited: true,
                superPremiumTokenUnlimited: true,
                reasoningTokenUnlimited: true,
                standardCallUnlimited: false,
                premiumCallUnlimited: false,
                superPremiumCallUnlimited: false,
                reasoningCallUnlimited: false,
              },
            })
          ) as Promise<GraphQLResult<CreateLLMUsageTierMutation>>;
        };

        await usageTierCreate();
      }

      const highPowerGroup = await createLLMGroupAfterEdit({
        name: "Premium Models",
        status: "active",
        projectId: projectId,
        description: "High power models for complex tasks.",
        systemInstructions: "",
        systemInstructionsAction: "append",
        order: 0,
      });

      await createLLMGroupAfterEdit({
        name: "Private Models",
        status: "active",
        projectId: projectId,
        description: "Internally hosted or fine-tuned models.",
        systemInstructions: "",
        systemInstructionsAction: "append",
        order: 0,
      });

      await createLLMServiceAfterEdit({
        name: "Open AI - GPT5-mini",
        status: "active",
        vendor: "openai",
        region: "global",
        endpoint: openAI_EndpointTemplate,
        body: openAI_bodyTemplate.replace("gpt-4o", "gpt-5-mini"),
        header: openAI_headerTemplate,
        loadProportion: 100,
        projectId: projectId,
        inputTokenPricePerMillion: 0.15,
        outputTokenPricePerMillion: 0.6,
      });

      await createLLMServiceAfterEdit({
        name: "Anthropic - Claude-3.5-Haiku",
        status: "active",
        vendor: "anthropic",
        region: "global",
        endpoint: anthropic_EndpointTemplate,
        body: anthropic_bodyTemplate.replace(
          "claude-3-7-sonnet-latest",
          "claude-3-5-haiku-latest"
        ),
        header: anthropic_headerTemplate,
        loadProportion: 100,
        projectId: projectId,
        inputTokenPricePerMillion: 0.8,
        outputTokenPricePerMillion: 4,
      });

      await createLLMServiceAfterEdit({
        name: "Google - Gemini 2.0 Flash Lite",
        status: "active",
        vendor: "google",
        region: "global",
        endpoint: google_EndpointTemplate.replace(
          "gemini-2.0-flash",
          "gemini-2.0-flash-lite"
        ),
        body: google_bodyTemplate,
        header: google_headerTemplate,
        loadProportion: 100,
        projectId: projectId,
        inputTokenPricePerMillion: 0.075,
        outputTokenPricePerMillion: 0.3,
      });

      // add openAI high power service
      await createLLMServiceAfterEdit({
        name: "Open AI - GPT5",
        status: "active",
        vendor: "openai",
        region: "global",
        endpoint: openAI_EndpointTemplate,
        body: openAI_bodyTemplate.replace("gpt-4o", "gpt-5"),
        header: openAI_headerTemplate,
        loadProportion: 100,
        projectId: projectId,
        groupId: highPowerGroup.data?.createLLMGroup?.id ?? "",
        inputTokenPricePerMillion: openAI_inputPricePerMillion,
        outputTokenPricePerMillion: openAI_outputPricePerMillion,
      });

      await createLLMServiceAfterEdit({
        name: "Anthropic - Claude-3.7-Sonnet",
        status: "active",
        vendor: "anthropic",
        region: "global",
        endpoint: anthropic_EndpointTemplate,
        body: anthropic_bodyTemplate,
        header: anthropic_headerTemplate,
        loadProportion: 100,
        projectId: projectId,
        groupId: highPowerGroup.data?.createLLMGroup?.id ?? "",
        inputTokenPricePerMillion: anthropic_inputPricePerMillion,
        outputTokenPricePerMillion: anthropic_outputPricePerMillion,
      });

      await createLLMServiceAfterEdit({
        name: "Google - Gemini 2.0 Flash",
        status: "active",
        vendor: "google",
        region: "global",
        endpoint: google_EndpointTemplate,
        body: google_bodyTemplate,
        header: google_headerTemplate,
        loadProportion: 100,
        projectId: projectId,
        groupId: highPowerGroup.data?.createLLMGroup?.id ?? "",
        inputTokenPricePerMillion: google_inputPricePerMillion,
        outputTokenPricePerMillion: google_outputPricePerMillion,
      });
    }
  } catch (err: any) {
    console.error(err);
  }

  return newUser;
}
